import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PendingApproval from '../pages/main/PendingApproval';
import { getSafeApiUrl, isDeveloperMode } from '../config/environment';
import AccountSuspended from '../pages/AccountSuspended';
import InstallationWaiting from '../pages/InstallationWaiting';
import { installationApi } from '../services/installationApi';
import { subscriptionApi } from '../services/subscriptionApi';
import dayjs from 'dayjs';

interface ProtectedRouteProps {
  children?: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { currentUser, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [userStatus, setUserStatus] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string | null>(null);
  const [installationStatus, setInstallationStatus] = useState<'checking' | 'not_completed' | 'completed' | 'not_required'>('checking');
  const [externalPeriodEndedRedirect, setExternalPeriodEndedRedirect] = useState(false);
  const [externalSuspendedRedirect, setExternalSuspendedRedirect] = useState(false);
  const [subscriptionExpiredRedirect, setSubscriptionExpiredRedirect] = useState(false);
  const [subscriptionStartPending, setSubscriptionStartPending] = useState(false);

  // 서버 점검 모드 체크
  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        const gatewayUrl = getSafeApiUrl('GATEWAY');
        // getSafeApiUrl이 이미 /api를 포함하므로 /api를 추가하지 않음
        const maintenanceUrl = `${gatewayUrl}/server-maintenance-status`;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        const resp = await fetch(maintenanceUrl, { signal: controller.signal });
        clearTimeout(timeoutId);
        
        if (resp.ok) {
          const data = await resp.json();
          if (data.isMaintenanceMode) {
            // 관리자 페이지는 점검 모드에서도 접근 가능
            const isAdminPath = window.location.pathname.startsWith('/admin');
            if (!isAdminPath) {
              navigate('/server-maintenance', { replace: true });
            }
          }
        }
      } catch (error) {
        // 점검 모드 체크 실패 시에도 정상 진행 (네트워크 오류 등)
        console.warn('서버 점검 모드 체크 실패:', error);
      }
    };

    checkMaintenanceMode();
  }, [navigate]);

  // 세팅 완료 여부 확인
  useEffect(() => {
    const checkInstallationStatus = async () => {
      if (!currentUser || !isAuthenticated) {
        setInstallationStatus('not_required');
        setSubscriptionStartPending(false);
        return;
      }
      setSubscriptionStartPending(false);

      // installation-waiting 페이지는 본사만 체크 스킵 (외부는 이용 기간 검사 필요)
      const isExternal = currentUser?.registrationSource === '외부';
      if (location.pathname === '/installation-waiting' && !isExternal) {
        setInstallationStatus('not_required');
        return;
      }

      try {
        const userId = currentUser.id || parseInt(localStorage.getItem('storeId') || '0');
        
        // 구독 정보 조회 (세팅 완료 여부 확인)
        try {
          const subscriptionResponse = await subscriptionApi.getSubscriptionByUserId(userId, {
            registrationSource: currentUser?.registrationSource ?? undefined,
            skipApiForExternal: !isExternal, // 외부 사용자는 이용 기간 확인을 위해 실제 API 호출
          });
          const subscription = subscriptionResponse.data;

          if (isExternal) {
            // 외부결제: endDate 만료 시 구독 상태에 따라 분기 (재시도 기간 중 이용 허용, 3차 실패 시 중지)
            const endDateStr = subscription?.endDate;
            if (!endDateStr) {
              setInstallationStatus('completed');
              return;
            }
            const endDate = new Date(endDateStr);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);
            if (endDate < today) {
              const status = subscription?.status?.toLowerCase?.() ?? '';
              if (status === 'payment_failed') {
                setInstallationStatus('completed');
                return;
              }
              if (status === 'suspended') {
                setExternalSuspendedRedirect(true);
                return;
              }
              setExternalPeriodEndedRedirect(true);
              return;
            }
            setInstallationStatus('completed');
            return;
          }
          
          if (subscription && subscription.status) {
            // 본사: 구독 만료 시 재등록 유도 페이지로
            if (subscription.status === 'expired') {
              console.log('ProtectedRoute - 본사 구독 만료 → 플랜선택/카드등록으로 이동');
              setSubscriptionExpiredRedirect(true);
              return;
            }
            // 본사: 구독 상태가 trial 이상이면 세팅 완료된 것으로 간주
            if (['trial', 'active', 'paused', 'payment_failed', 'cancelled'].includes(subscription.status)) {
              setInstallationStatus('completed');
              // 이용 시작일이 미래로 설정된 경우 (관리자가 구독 목록에서 시작일을 예약한 경우)
              const startDateStr = subscription.startDate ?? (subscription as { StartDate?: string }).StartDate;
              const startDay = startDateStr ? dayjs(startDateStr).startOf('day') : null;
              const todayStart = dayjs().startOf('day');
              const isValid = startDay?.isValid() ?? false;
              const isFuture = isValid && (startDay?.isAfter(todayStart) ?? false);
              console.log('[ProtectedRoute] 구독 시작일 체크', {
                startDateStr: startDateStr ?? '(없음)',
                startDay: startDay?.format?.('YYYY-MM-DD') ?? '(파싱안함)',
                todayStart: todayStart.format('YYYY-MM-DD'),
                isValid,
                isFuture,
                subscriptionStartPending설정: isFuture
              });
              if (startDateStr && isValid && isFuture) setSubscriptionStartPending(true);
              return;
            }
          }
        } catch (error: any) {
          if (isExternal) {
            // 외부 사용자 구독 조회 실패(401 등) → 기간 만료로 간주하고 플랜선택으로
            console.log('ProtectedRoute - 외부결제 구독 조회 실패 → 플랜선택/카드등록으로 이동');
            setExternalPeriodEndedRedirect(true);
            return;
          }
          if (error.response?.status === 404) {
            console.log('구독 정보 없음 - 세팅 일정 확인');
          } else {
            console.error('구독 정보 조회 실패:', error);
          }
        }

        // 세팅 일정 조회
        try {
          const installationResponse = await installationApi.getInstallationRequestByUserId(userId);
          const installation = installationResponse.data;
          
          if (installation) {
            if (installation.status === 'completed') {
              setInstallationStatus('completed');
            } else {
              setInstallationStatus('not_completed');
            }
          } else {
            // 세팅 일정이 없으면 완료된 것으로 간주 (기존 사용자)
            setInstallationStatus('completed');
          }
        } catch (error: any) {
          // 세팅 일정이 없거나 에러 발생 (404 등)
          if (error.response?.status === 404) {
            // 세팅 일정이 없으면 완료된 것으로 간주 (기존 사용자)
            setInstallationStatus('completed');
          } else {
            console.error('세팅 일정 조회 실패:', error);
            setInstallationStatus('completed'); // 에러 시 기본값
          }
        }
      } catch (error) {
        console.error('세팅 완료 여부 확인 실패:', error);
        setInstallationStatus('completed'); // 에러 시 기본값
      }
    };

    if (currentUser && isAuthenticated) {
      checkInstallationStatus();
    }
  }, [currentUser, isAuthenticated, location.pathname]);

  // 사용자 상태 확인
  useEffect(() => {
    if (currentUser) {
      console.log('ProtectedRoute - currentUser 정보:', currentUser);
      console.log('ProtectedRoute - status:', currentUser.status);
      console.log('ProtectedRoute - rejectionReason:', currentUser.rejectionReason);
      console.log('ProtectedRoute - approvedAt:', currentUser.approvedAt);
      
      const status = currentUser.status || null;
      const reason = currentUser.rejectionReason || null;
      
      setUserStatus(status);
      setRejectionReason(reason);
      
      // 즉시 상태 확인 및 처리
      if (status === 'pending') {
        console.log('ProtectedRoute - 즉시 pending 상태 감지');
      } else if (status === 'rejected') {
        console.log('ProtectedRoute - 즉시 rejected 상태 감지');
      } else if (status === 'approved' || !status) {
        console.log('ProtectedRoute - 즉시 approved 상태 감지');
      }
    }
  }, [currentUser]);

  // 로딩 중이면 로딩 표시
  if (loading) {
    return <div>로딩 중...</div>;
  }

  // 인증되지 않은 경우 - 더 관대한 처리
  if (!isAuthenticated) {
    // 개발 모드에서 Mock 사용자 활성화 체크
    const isDevMode = process.env.NODE_ENV === 'development';
    const enableMockUser = localStorage.getItem('enableMockUser') === 'true';
    
    if (isDevMode && enableMockUser) {
      console.log('🛠️ [개발 모드] Mock 사용자 모드 - 인증 우회');
      // Mock 사용자 모드에서는 인증 체크를 건너뛰고 바로 접근 허용
      return <>{children || <Outlet />}</>;
    }
    
    const token = localStorage.getItem('token');
    const storeId = localStorage.getItem('storeId');
    
    // 토큰은 있지만 currentUser가 없는 경우 (세션 복원 중)
    if (token && storeId && !currentUser) {
      console.log('토큰은 있지만 사용자 정보가 없음 - 세션 복원 대기 중...');
      return <div>인증 확인 중...</div>;
    }
    
    // 토큰이나 storeId가 없는 경우
    if (!token || !storeId) {
      console.log('토큰 또는 storeId가 없음 - 로그인 페이지로 이동');
      return <Navigate to="/login" replace />;
    }
    
    // 기타 경우
    return <Navigate to="/login" replace />;
  }

  // 사용자 상태에 따른 화면 표시 (대시보드 로딩 방지)
  // 백엔드가 status/approvalStatus 둘 다 줄 수 있고, enum을 숫자(0=pending,1=approved,2=rejected,3=suspended)로 줄 수 있음
  const rawStatus: unknown = currentUser?.status ?? currentUser?.approvalStatus ?? userStatus;
  const currentStatus: string =
    rawStatus === 0 || rawStatus === '0'
      ? 'pending'
      : rawStatus === 1 || rawStatus === '1'
        ? 'approved'
        : rawStatus === 2 || rawStatus === '2'
          ? 'rejected'
          : rawStatus === 3 || rawStatus === '3'
            ? 'suspended'
            : typeof rawStatus === 'string'
              ? rawStatus.toLowerCase()
              : String(rawStatus ?? '');
  
  console.log('ProtectedRoute - 최종 상태 확인:', currentStatus);
  console.log('ProtectedRoute - currentUser?.status:', currentUser?.status);
  console.log('ProtectedRoute - userStatus:', userStatus);
  console.log('ProtectedRoute - currentUser?.isActive:', currentUser?.isActive);
  
  // IsActive가 false인 경우 로그인 페이지로 리다이렉트
  if (currentUser && currentUser.isActive === false) {
    console.log('ProtectedRoute - 비활성 계정: 로그인 페이지로 리다이렉트');
    return <Navigate to="/login" replace />;
  }
  
  const rawStep = currentUser?.registrationStep as number | string | null | undefined;
  const registrationSource = currentUser?.registrationSource;
  const step = rawStep != null && rawStep !== '' ? Number(rawStep) : null;

  // 중지(비활성) 계정은 먼저 처리
  if (currentStatus === 'suspended') {
    return <AccountSuspended rejectionReason={rejectionReason || undefined} suspendedAt={currentUser?.approvedAt} />;
  }

  // 미완료 가입 플로우: step 0, 1 또는 본사 step 2만. 외부(와디즈) step 2 = 승인 대기 → pending-approval-external (개발자 모드에서는 건너뜀)
  if (!isDeveloperMode() && (step === 0 || step === 1 || (step === 2 && registrationSource !== '외부'))) {
    console.log('ProtectedRoute - 미완료 가입 플로우: ContinueRegistration으로 리다이렉트', { step });
    return <Navigate to="/continue-registration" replace />;
  }

  // 승인 상태 검사 (가입 완료(step 3) 또는 기존 사용자만 해당)
  if (currentStatus === 'pending') {
    if (registrationSource === '외부') {
      console.log('ProtectedRoute - pending 상태(외부): PendingApprovalExternal로 리다이렉트');
      return <Navigate to="/pending-approval-external" replace />;
    }
    console.log('ProtectedRoute - pending 상태: PendingApproval 화면 표시');
    return <PendingApproval userStatus="pending" />;
  }
  if (currentStatus === 'rejected') {
    if (registrationSource === '외부') {
      console.log('ProtectedRoute - rejected 상태(외부): PendingApprovalExternal로 리다이렉트');
      return <Navigate to="/pending-approval-external" replace />;
    }
    return <PendingApproval userStatus="rejected" rejectionReason={rejectionReason || undefined} />;
  }

  // 외부 전환 3차 실패 등으로 구독 suspended: account-suspended로 유도
  if (externalSuspendedRedirect) {
    return <Navigate to="/account-suspended" replace />;
  }

  // 외부결제 이용 기간 만료: 플랜선택·카드등록 플로우로 유도
  if (externalPeriodEndedRedirect) {
    return <Navigate to="/continue-registration" replace state={{ externalPeriodEnded: true }} />;
  }

  // 본사 구독 만료: 플랜선택·카드등록 플로우로 유도
  if (subscriptionExpiredRedirect) {
    return <Navigate to="/continue-registration" replace state={{ subscriptionExpired: true }} />;
  }

  // 정상 사용자만 대시보드 접근 허용
  if (currentStatus === 'approved' || !currentStatus) {
    // 개발자 모드(localhost:3000): 사업자정보·플랜선택·세팅 완료 등 절차 없이 바로 대시보드 진입
    if (isDeveloperMode()) {
      console.log('ProtectedRoute - 개발자 모드: 대시보드 바로 진입');
      return <Outlet />;
    }

    // 기존 사용자 호환: 사업자 정보 완료 여부 확인 (pending/rejected/suspended 체크 이후, installationStatus 체크 이전)
    // 새로운 회원가입 플로우(RegistrationStep이 Completed=3가 아닌 경우)에서는 hasBusinessInfo 체크를 건너뜀
    // 백엔드에서 enum이 숫자로 직렬화됨: 0=AccountCreated, 1=CardRegistered, 2=InstallationRequested, 3=Completed
    // hasBusinessInfo가 false이거나 undefined인 경우 business-info로 리다이렉션
    if (currentUser && (currentUser.hasBusinessInfo === false || currentUser.hasBusinessInfo === undefined)) {
      // RegistrationStep이 Completed(3)가 아닌 경우(새로운 회원가입 플로우)는 사업자 정보 체크 건너뛰기
      const userStepRaw = currentUser.registrationStep as number | string | null | undefined;
      const userStep = userStepRaw != null && userStepRaw !== '' ? Number(userStepRaw) : null;
      if (userStep === null || userStep === 3) {
        console.log('ProtectedRoute - 사업자 정보 미완료: BusinessInfo 페이지로 리다이렉트', { hasBusinessInfo: currentUser.hasBusinessInfo });
        // business-info 페이지는 ProtectedRoute 밖에 있으므로 직접 navigate
        // 하지만 ProtectedRoute 내부에서는 Navigate 컴포넌트 사용
        return <Navigate to="/business-info" replace />;
      }
    }
    
    // 세팅 완료 여부 확인
    if (installationStatus === 'checking') {
      console.log('ProtectedRoute - 세팅 완료 여부 확인 중...');
      return <div>확인 중...</div>;
    }
    
    if (installationStatus === 'not_completed') {
      console.log('ProtectedRoute - 세팅 미완료: InstallationWaiting 페이지로 리다이렉트');
      return <InstallationWaiting />;
    }
    
    // 이용 시작일이 미래로 설정된 경우: 해당 날짜 안내 페이지로 이동 (다른 경로 접근 시)
    if (subscriptionStartPending && location.pathname !== '/subscription-starts-on') {
      console.log('ProtectedRoute - 이용 시작일 미도래: subscription-starts-on으로 리다이렉트');
      return <Navigate to="/subscription-starts-on" replace />;
    }
    
    console.log('ProtectedRoute - 정상 사용자: 대시보드 접근 허용');
    return <Outlet />;
  }

  // 기타 상태 (예: banned 등) - 기본적으로 거절 화면 표시
  console.log('ProtectedRoute - 기타 상태:', currentStatus, '거절 화면 표시');
  return <PendingApproval userStatus="rejected" rejectionReason={rejectionReason || undefined} />;
};

export default ProtectedRoute; 